import React, { useMemo, useRef, useState } from "react";

import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";

import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/ModeEdit";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import PreviewIcon from "@mui/icons-material/Preview";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";

import { usePutSubmitMockDrillReportMutation } from "apis/FireSafety/FireSafetyMockDrill/fireSafetyMockDrill";

import getFileExtension from "utils/getFileExtension";
import { getImageUrl } from "utils";

import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";

import MockDrillDetailModal from "./MockDrillDetailModal";

const FireSafetyMockDrillActions = ({
  item,
  isAdmin,
  isSrManager,
  onEdit,
  viewMockDrillDetailhandler,
  t,
}) => {
  const { id, status, mockDrillDetail } = item;
  const documentUploadRef = useRef();

  const [SelectedDrillId, setSelectedDrillId] = useState(null);
  const [documentToPreview, setDocumentToPreview] = useState();
  const [uploadedFile, setUploadedFile] = useState();
  const [mockDrillReportPreview, setMockDrillReportPreview] = useState(null);

  const [uploadDocument, { isLoading }] = usePutSubmitMockDrillReportMutation();

  const statusScheduled = useMemo(() => status === 1, [status]);
  const isDeveloperIntimation = useMemo(() => status === 2, [status]);
  const isReportSubmited = useMemo(() => status === 3, [status]);

  const handleClose = () => {
    setDocumentToPreview(null);
    setUploadedFile(null);
    documentUploadRef.current.value = "";
  };
  const handleUploadFile = async (event) => {
    try {
      const fileExtension = getFileExtension(
        event.target.files[0]?.name
      ).toLowerCase();
      const fileSize = event.target.files[0]?.size;
      if (fileSize > 10485760) {
        toast.error("File size should be less than 10MB");
      } else if (fileExtension !== "pdf") {
        toast.error("Invalid file format, Please upload pdf file");
      } else {
        setDocumentToPreview({
          url: URL.createObjectURL(event.target.files[0]),
          documentTypeName: "Submit Mock Drill Report",
          documentType: "pdf",
          documentName: "submitMockDrillReport",
        });
        setUploadedFile(event.target.files[0]);
      }
      event.target.value = "";
    } catch (err) {}
  };
  const uploadDocumentBtnHandler = async () => {
    try {
      const formData = new FormData();
      formData.append("file", uploadedFile);
      await uploadDocument(formData, { mockDrillScheduleId: id });
      handleClose();
    } catch (error) {}
  };
  const handleMockDrillReport = () => {
    const reportUrl = mockDrillDetail?.mockDrillReportUrl;
    if (reportUrl) {
      setMockDrillReportPreview({
        url: reportUrl ? getImageUrl(reportUrl) : null,
        documentTypeName: "Mock Drill Report",
        documentType: "pdf",
        documentName: "MockDrillReport",
      });
    }
  };

  return (
    <>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={handleClose}
          isDownloadable={false}
          actionButton={
            <Button
              onClick={uploadDocumentBtnHandler}
              color="primary"
              className="mb-1 me-1"
              variant="contained"
              disabled={isLoading}
            >
              {t("Upload")} {isLoading && <CircularProgress size={20} />}
            </Button>
          }
          {...documentToPreview}
        />
      )}
      {!!mockDrillReportPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => {
            setMockDrillReportPreview(null);
          }}
          isDownloadable={false}
          {...mockDrillReportPreview}
        />
      )}
      {!!SelectedDrillId && (
        <MockDrillDetailModal
          item={item}
          isOpen={!!SelectedDrillId}
          id={SelectedDrillId}
          handleClose={() => {
            setSelectedDrillId(null);
          }}
        />
      )}

      <Box sx={{ minWidth: "100px" }}>
        <IconButton onClick={() => viewMockDrillDetailhandler(id)}>
          <Tooltip title={t("View Mock Drill Details")}>
            <RemoveRedEyeIcon
              fontSize="small"
              color="primary"
              className="cursor-pointer"
            />
          </Tooltip>
        </IconButton>

        {isSrManager && (
          <Tooltip title={t("Edit schedule")}>
            <IconButton
              onClick={() => {
                onEdit(id);
              }}
              disabled={!statusScheduled}
            >
              <EditIcon
                color={!statusScheduled ? "disabled" : "primary"}
                fontSize="small"
              />
            </IconButton>
          </Tooltip>
        )}

        {!isAdmin && (
          <Tooltip title={t("Mock drill Detail")}>
            <IconButton
              disabled={!statusScheduled}
              onClick={() => {
                setSelectedDrillId(id);
              }}
            >
              <LibraryAddIcon
                color={!statusScheduled ? "disabled" : "primary"}
                fontSize="small"
              />
            </IconButton>
          </Tooltip>
        )}

        {!isAdmin && (
          <IconButton disabled={!isDeveloperIntimation} component="label">
            <Tooltip title={t("Submit Mock Drill Report")}>
              <div>
                <FileUploadRoundedIcon
                  color={!isDeveloperIntimation ? "disabled" : "primary"}
                  fontSize="small"
                />

                <input
                  style={{
                    display: "none",
                  }}
                  ref={documentUploadRef}
                  accept=".pdf"
                  type="file"
                  onChange={(e) => handleUploadFile(e)}
                />
              </div>
            </Tooltip>
          </IconButton>
        )}

        {isReportSubmited && (
          <IconButton
            onClick={() => {
              handleMockDrillReport();
            }}
          >
            <Tooltip title={t("Mock Drill Report")}>
              <PreviewIcon
                fontSize="small"
                color="primary"
                className="cursor-pointer"
              />
            </Tooltip>
          </IconButton>
        )}
      </Box>
    </>
  );
};

FireSafetyMockDrillActions.propTypes = {
  item: PropTypes.object,
  isAdmin: PropTypes.bool,
  isSrManager: PropTypes.bool,
  onEdit: PropTypes.func,
  viewMockDrillDetailhandler: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(FireSafetyMockDrillActions);
